import type { PracticeSearchIndex, PracticeSearchTopic } from "../api/progress";
import { COMECANDO_BELOW, DOMINADO_FROM } from "./mastery";

export const PRACTICE_ESTADOS = ["reforcar", "nao-praticado", "dominado"] as const;

export type PracticeEstado = (typeof PRACTICE_ESTADOS)[number];

/**
 * The Praticar page's filters as its URL carries them. Defaults are absent.
 * `materia` is the taxonomy subjectId, as on the session route.
 */
export interface PracticeFilterSearch {
  materia?: number;
  tema?: number;
  estado?: PracticeEstado;
  trilha?: true;
}

/** The filters resolved against the caderno: the matéria as its yearSubjectId */
export interface TopicFilters {
  yearSubjectId: number | null;
  temaId: number | null;
  estado: PracticeEstado | null;
  trilha: boolean;
}

export const NO_FILTERS: TopicFilters = {
  yearSubjectId: null,
  temaId: null,
  estado: null,
  trilha: false,
};

/**
 * "A reforçar": practised, and the student is still "Começando" on it. The same
 * rule the old Praticar used for matérias, applied per tópico.
 */
export function isReforcar(mastery: number | null): boolean {
  return mastery !== null && mastery < COMECANDO_BELOW;
}

export function isDominado(mastery: number | null): boolean {
  return mastery !== null && mastery >= DOMINADO_FROM;
}

export function matchesEstado(mastery: number | null, estado: PracticeEstado): boolean {
  if (estado === "reforcar") {
    return isReforcar(mastery);
  }
  if (estado === "dominado") {
    return isDominado(mastery);
  }

  return mastery === null;
}

export function isFiltering(filters: TopicFilters): boolean {
  return (
    filters.yearSubjectId !== null ||
    filters.temaId !== null ||
    filters.estado !== null ||
    filters.trilha
  );
}

export function passesFilters(
  topic: PracticeSearchTopic,
  filters: TopicFilters
): boolean {
  return (
    (filters.yearSubjectId === null || topic.yearSubjectId === filters.yearSubjectId) &&
    (filters.temaId === null || topic.temaId === filters.temaId) &&
    (filters.estado === null || matchesEstado(topic.mastery, filters.estado)) &&
    (!filters.trilha || topic.opened)
  );
}

/**
 * The URL filters against the caderno's matérias and index. A matéria the caderno
 * does not hold is ignored, and so is a tema outside the chosen matéria.
 */
export function resolveFilters(
  search: PracticeFilterSearch,
  subjects: { subjectId: number | null; yearSubjectId: number }[],
  index: PracticeSearchIndex | undefined
): TopicFilters {
  const subject =
    search.materia === undefined
      ? undefined
      : subjects.find((candidate) => candidate.subjectId === search.materia);
  const yearSubjectId = subject?.yearSubjectId ?? null;
  const temaKnown =
    index === undefined ||
    index.temas.some(
      (tema) => tema.termId === search.tema && tema.yearSubjectId === yearSubjectId
    );

  return {
    yearSubjectId,
    temaId:
      yearSubjectId !== null && search.tema !== undefined && temaKnown
        ? search.tema
        : null,
    estado: search.estado ?? null,
    trilha: search.trilha === true,
  };
}

export function validatePracticeFilterSearch(
  search: Record<string, unknown>
): PracticeFilterSearch {
  const materia = Number(search.materia);
  const tema = Number(search.tema);
  const estado = PRACTICE_ESTADOS.find((value) => value === search.estado);
  const filters: PracticeFilterSearch = {};

  if (Number.isInteger(materia) && materia > 0) {
    filters.materia = materia;
    if (Number.isInteger(tema) && tema > 0) {
      filters.tema = tema;
    }
  }
  if (estado !== undefined) {
    filters.estado = estado;
  }
  if (search.trilha === true || search.trilha === "true" || search.trilha === 1) {
    filters.trilha = true;
  }

  return filters;
}
